import { useCallback, useEffect, useState } from 'react'

import { useLazyGetDocumentTypeByIdQuery } from '../../../store/ai/documentTypes'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import {
  InputParametersHierarchyDefault,
  useCreateNodeMutation,
  useLazyGetEffectiveNodeQuery,
  useLazyGetInputParametersHierarchyDefaultQuery,
} from '../../../store/ai/inputParametersHierarchy'

import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'

import { DocumentType } from '../../../models/DocumentType'

export const useDocumentType = (documentTypeId: string) => {
  const accountId = useAppSelector(selectAccountId)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [documentType, setDocumentType] = useState<DocumentType | null>(null)

  const [triggerGetDocumentTypeById] = useLazyGetDocumentTypeByIdQuery()

  const fetchDocumentType = useCallback(async () => {
    if (!documentTypeId) {
      return null
    }
    setIsLoading(true)
    try {
      const { data: documentType } = await triggerGetDocumentTypeById({ accountId, documentTypeId }).unwrap()
      if (!documentType) {
        return null
      }
      setDocumentType(documentType)
      return documentType
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
    return null
  }, [accountId, documentTypeId, triggerGetDocumentTypeById])

  useEffect(() => {
    fetchDocumentType()
  }, [fetchDocumentType])

  return { documentType, isLoading, fetchDocumentType }
}

interface UseCopyDocumentTemplateProps {
  documentType: DocumentType | null
}

export const useCopyDocumentTemplate = ({ documentType }: UseCopyDocumentTemplateProps) => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetIpHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()
  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [createInputParametersHierarchyNode] = useCreateNodeMutation()

  const [ipHierarchyDefault, setIpHierarchyDefault] = useState<InputParametersHierarchyDefault | null>(null)

  useEffect(() => {
    const fetchIpHierarchyDefault = async () => {
      if (!documentType) {
        return
      }
      const { systemDocumentTypeId } = documentType
      try {
        const { data: ipHierarchyDefaultResponse } = await triggerGetIpHierarchyDefault({
          accountId,
          type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE,
          typeId: systemDocumentTypeId ?? 'default-summary',
        })
        if (!ipHierarchyDefaultResponse) {
          setIpHierarchyDefault(null)
          return
        }
        setIpHierarchyDefault(ipHierarchyDefaultResponse)
      } catch (e) {
        console.error(e)
      }
    }
    fetchIpHierarchyDefault()
  }, [accountId, documentType, triggerGetIpHierarchyDefault])

  const createCopiedInputParameters = useCallback(
    async (type: InputParameterHierarchyType, typeId: string, newDocumentTypeId: string, inputParameters: { [key: string]: any }) => {
      try {
        await createInputParametersHierarchyNode({
          accountId,
          type,
          typeId,
          node: {
            hierarchyId: { level: 'document_type_global', documentTypeId: newDocumentTypeId },
            inputParameters,
          },
        }).unwrap()
      } catch (e) {
        console.error(e)
      }
    },
    [accountId, createInputParametersHierarchyNode],
  )

  const copyTemplate = useCallback(
    async (type: InputParameterHierarchyType, typeId: string, newDocumentTypeId: string) => {
      if (!documentType) {
        return
      }

      const fromSystemDocumentType = !!documentType.systemDocumentTypeId

      if (fromSystemDocumentType && ipHierarchyDefault) {
        await createCopiedInputParameters(type, typeId, newDocumentTypeId, ipHierarchyDefault.inputParameters)
        return
      }

      const { id: documentTypeId } = documentType

      const template = await getInputParameterHierarchyNode({
        accountId,
        type,
        typeId,
        context: { effective: false, level: 'document_type_global', documentTypeId, contextType: 'document_type' },
      }).unwrap()

      if (!template) {
        return
      }

      await createCopiedInputParameters(type, typeId, newDocumentTypeId, template.inputParameters)
    },
    [accountId, documentType, createCopiedInputParameters, getInputParameterHierarchyNode, ipHierarchyDefault],
  )

  const copyTemplates = useCallback(
    async (newDocumentTypeId: string) => {
      if (!documentType) {
        return
      }
      const templatesToCopy = [{ type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE, typeId: 'summary-template' }]
      await Promise.all(templatesToCopy.map(({ type, typeId }) => copyTemplate(type, typeId, newDocumentTypeId)))
    },
    [documentType, copyTemplate],
  )

  return { copyTemplates, ipHierarchyDefault }
}
